import { create } from 'zustand'
import { CNSExperiment } from '@/models/cns'
import { fetchExperiments as fetchExperimentsService, fetchExperiment as fetchExperimentService } from '@/services/experimentService'

interface ExperimentStore {
  experimentsByProject: Record<string, CNSExperiment[]>
  loadingByProject: Record<string, boolean>
  errorByProject: Record<string, string | null>

  experimentById: Record<string, CNSExperiment>
  loadingExperimentById: Record<string, boolean>
  errorExperimentById: Record<string, string | null>
  fetchPromiseById: Record<string, Promise<CNSExperiment>>

  fetchExperiments: (projectId: string) => Promise<void>
  fetchExperimentById: (projectId: string, experimentId: string) => Promise<CNSExperiment | null>
  getExperimentById: (experimentId: string) => CNSExperiment | undefined
  getExperimentLoading: (experimentId: string) => boolean
  getExperimentError: (experimentId: string) => string | null
  updateExperimentInCache: (experimentId: string, data: CNSExperiment) => void
  clearExperimentById: (experimentId: string) => void
  getExperiments: (projectId: string) => CNSExperiment[]
  clearExperiments: (projectId: string) => void
}

export const useExperimentStore = create<ExperimentStore>((set, get) => ({
  experimentsByProject: {},
  loadingByProject: {},
  errorByProject: {},

  experimentById: {},
  loadingExperimentById: {},
  errorExperimentById: {},
  fetchPromiseById: {},

  fetchExperiments: async (projectId: string) => {
    const state = get()

    if (state.loadingByProject[projectId]) {
      return
    }

    set((state) => {
      if (state.loadingByProject[projectId]) {
        return state
      }
      return {
        loadingByProject: { ...state.loadingByProject, [projectId]: true },
        errorByProject: { ...state.errorByProject, [projectId]: null },
      }
    })

    const currentState = get()
    if (currentState.loadingByProject[projectId] === false) {
      return
    }

    try {
      const experiments = await fetchExperimentsService(projectId)
      set((state) => ({
        experimentsByProject: { ...state.experimentsByProject, [projectId]: experiments },
        loadingByProject: { ...state.loadingByProject, [projectId]: false },
        errorByProject: { ...state.errorByProject, [projectId]: null },
      }))
    } catch (error: unknown) {
      set((state) => ({
        loadingByProject: { ...state.loadingByProject, [projectId]: false },
        errorByProject: {
          ...state.errorByProject,
          [projectId]: error instanceof Error ? error.message : 'Failed to fetch experiments',
        },
      }))
    }
  },

  fetchExperimentById: async (projectId: string, experimentId: string): Promise<CNSExperiment | null> => {
    const state = get()
    const inFlight = state.fetchPromiseById[experimentId]
    if (inFlight) return inFlight

    const isColdLoad = !state.experimentById[experimentId]

    const promise = (async () => {
      if (isColdLoad) {
        set((s) => ({
          loadingExperimentById: { ...s.loadingExperimentById, [experimentId]: true },
          errorExperimentById: { ...s.errorExperimentById, [experimentId]: null },
        }))
      }
      try {
        const data = await fetchExperimentService(projectId, experimentId)
        set((s) => ({
          experimentById: { ...s.experimentById, [experimentId]: data },
          loadingExperimentById: { ...s.loadingExperimentById, [experimentId]: false },
          errorExperimentById: { ...s.errorExperimentById, [experimentId]: null },
          fetchPromiseById: (() => {
            const next = { ...s.fetchPromiseById }
            delete next[experimentId]
            return next
          })(),
        }))
        return data
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to fetch experiment'
        set((s) => ({
          loadingExperimentById: { ...s.loadingExperimentById, [experimentId]: false },
          errorExperimentById: { ...s.errorExperimentById, [experimentId]: message },
          fetchPromiseById: (() => {
            const next = { ...s.fetchPromiseById }
            delete next[experimentId]
            return next
          })(),
        }))
        throw err
      }
    })()
    set((s) => ({ fetchPromiseById: { ...s.fetchPromiseById, [experimentId]: promise } }))
    try {
      return await promise
    } catch {
      return null
    }
  },

  getExperimentById: (experimentId: string) => get().experimentById[experimentId],
  getExperimentLoading: (experimentId: string) => get().loadingExperimentById[experimentId] ?? false,
  getExperimentError: (experimentId: string) => get().errorExperimentById[experimentId] ?? null,

  updateExperimentInCache: (experimentId: string, data: CNSExperiment) => {
    set((s) => ({
      experimentById: { ...s.experimentById, [experimentId]: data },
    }))
  },

  clearExperimentById: (experimentId: string) => {
    set((s) => {
      const nextById = { ...s.experimentById }
      const nextLoading = { ...s.loadingExperimentById }
      const nextError = { ...s.errorExperimentById }
      const nextPromise = { ...s.fetchPromiseById }
      delete nextById[experimentId]
      delete nextLoading[experimentId]
      delete nextError[experimentId]
      delete nextPromise[experimentId]
      return { experimentById: nextById, loadingExperimentById: nextLoading, errorExperimentById: nextError, fetchPromiseById: nextPromise }
    })
  },

  getExperiments: (projectId: string) => {
    return get().experimentsByProject[projectId] || []
  },

  clearExperiments: (projectId: string) => {
    set((state) => {
      const newExperimentsByProject = { ...state.experimentsByProject }
      const newLoadingByProject = { ...state.loadingByProject }
      const newErrorByProject = { ...state.errorByProject }
      delete newExperimentsByProject[projectId]
      delete newLoadingByProject[projectId]
      delete newErrorByProject[projectId]
      return {
        experimentsByProject: newExperimentsByProject,
        loadingByProject: newLoadingByProject,
        errorByProject: newErrorByProject,
      }
    })
  },
}))
